import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { updateProfile } from '../api/auth';
import { listMyOrders, getMyOrder } from '../api/orders';

function formatPrice(cents) {
  return '$' + (cents / 100).toFixed(2);
}

export default function Account() {
  const { user, setUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [profileForm, setProfileForm] = useState({ name: '', password: '' });
  const [profileMsg, setProfileMsg] = useState('');
  const [addressForm, setAddressForm] = useState({ name: '', line1: '', line2: '', city: '', state: '', zip: '', country: 'US' });
  const [addressMsg, setAddressMsg] = useState('');

  useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name || '', password: '' });
      const addr = user.shipping_address || {};
      setAddressForm({
        name: addr.name || user.name || '',
        line1: addr.line1 || '', line2: addr.line2 || '',
        city: addr.city || '', state: addr.state || '',
        zip: addr.zip || '', country: addr.country || 'US',
      });
      listMyOrders()
        .then(res => setOrders(res.data))
        .catch(() => {})
        .finally(() => setLoadingOrders(false));
    }
  }, [user]);

  if (authLoading || !user) return null;

  const handleProfileSave = async (e) => {
    e.preventDefault();
    const data = {};
    if (profileForm.name !== user.name) data.name = profileForm.name;
    if (profileForm.password) data.password = profileForm.password;
    if (!Object.keys(data).length) return;
    try {
      const res = await updateProfile(data);
      setUser(res.data);
      setProfileMsg('Profile updated');
      setProfileForm(f => ({ ...f, password: '' }));
      setTimeout(() => setProfileMsg(''), 3000);
    } catch (err) {
      setProfileMsg(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleAddressSave = async (e) => {
    e.preventDefault();
    try {
      const res = await updateProfile({ shipping_address: addressForm });
      setUser(res.data);
      setAddressMsg('Address saved');
      setTimeout(() => setAddressMsg(''), 3000);
    } catch (err) {
      setAddressMsg(err.response?.data?.detail || 'Save failed');
    }
  };

  const viewOrder = async (orderNumber) => {
    try {
      const res = await getMyOrder(orderNumber);
      setSelectedOrder(res.data);
    } catch {
      // fallback to list data
      setSelectedOrder(orders.find(o => o.order_number === orderNumber));
    }
  };

  const tabs = [
    { key: 'orders', label: 'Order History' },
    { key: 'addresses', label: 'Addresses' },
    { key: 'profile', label: 'Profile' },
  ];

  return (
    <div className="container section">
      <h1 style={{ marginBottom: '8px' }}>My Account</h1>
      <p style={{ color: 'var(--warm-gray)', marginBottom: '32px' }}>{user.email}</p>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '32px', borderBottom: '2px solid var(--light-gray)', overflowX: 'auto' }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setSelectedOrder(null); }}
            className="mono"
            style={{
              padding: '12px 0', border: 'none', background: 'none', cursor: 'pointer',
              borderBottom: tab === t.key ? '2px solid var(--forest)' : '2px solid transparent',
              color: tab === t.key ? 'var(--ink)' : 'var(--warm-gray)',
              marginBottom: '-2px', whiteSpace: 'nowrap',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Orders Tab ── */}
      {tab === 'orders' && !selectedOrder && (
        loadingOrders ? <p style={{ color: 'var(--warm-gray)' }}>Loading orders...</p> :
        orders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ color: 'var(--warm-gray)', marginBottom: '16px' }}>No orders yet.</p>
            <Link to="/shop" className="btn btn-primary">Start Shopping</Link>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>Order</th><th>Date</th><th>Status</th><th>Total</th><th></th></tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.order_number}>
                  <td><strong>{o.order_number}</strong></td>
                  <td>{new Date(o.created_at).toLocaleDateString()}</td>
                  <td><span className={`badge badge-${o.status}`}>{o.status}</span></td>
                  <td>{formatPrice(o.total_cents)}</td>
                  <td>
                    <button className="btn btn-sm btn-secondary" onClick={() => viewOrder(o.order_number)}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {/* ── Order Detail ── */}
      {tab === 'orders' && selectedOrder && (
        <div>
          <button
            onClick={() => setSelectedOrder(null)}
            className="mono"
            style={{ background: 'none', border: 'none', color: 'var(--warm-gray)', marginBottom: '24px', cursor: 'pointer' }}
          >
            &larr; Back to Orders
          </button>

          <div className="cart-summary" style={{ maxWidth: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <span className="mono" style={{ color: 'var(--warm-gray)' }}>Order Number</span>
                <p style={{ fontWeight: '700', fontSize: '1.1rem' }}>{selectedOrder.order_number}</p>
              </div>
              <div>
                <span className="mono" style={{ color: 'var(--warm-gray)' }}>Date</span>
                <p>{new Date(selectedOrder.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <span className="mono" style={{ color: 'var(--warm-gray)' }}>Status</span>
                <p><span className={`badge badge-${selectedOrder.status}`}>{selectedOrder.status}</span></p>
              </div>
            </div>

            {selectedOrder.tracking_number && (
              <div style={{ background: 'var(--cream)', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px' }}>
                <span className="mono" style={{ color: 'var(--warm-gray)' }}>Tracking Number</span>
                <p style={{ fontWeight: '600', marginTop: '4px' }}>{selectedOrder.tracking_number}</p>
              </div>
            )}

            {selectedOrder.shipping_address && (
              <div style={{ marginBottom: '16px' }}>
                <span className="mono" style={{ color: 'var(--warm-gray)' }}>Ship To</span>
                <p style={{ marginTop: '4px' }}>
                  {selectedOrder.shipping_address.name}<br />
                  {selectedOrder.shipping_address.line1}<br />
                  {selectedOrder.shipping_address.line2 && <>{selectedOrder.shipping_address.line2}<br /></>}
                  {selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} {selectedOrder.shipping_address.zip}
                </p>
              </div>
            )}

            <div style={{ borderTop: '1px solid var(--light-gray)', paddingTop: '16px' }}>
              {selectedOrder.items?.map((item, i) => (
                <div className="cart-item" key={i}>
                  <div className="cart-item-details">
                    <div className="cart-item-name">{item.product_name}</div>
                    {(item.variant || item.color) && <div className="cart-item-variant">{item.variant || item.color}</div>}
                    <div className="cart-item-variant">Qty: {item.quantity}</div>
                  </div>
                  <div className="cart-item-price">{formatPrice(item.price_cents * item.quantity)}</div>
                </div>
              ))}
            </div>

            <div className="cart-totals">
              <div className="cart-total-row"><span>Subtotal</span><span>{formatPrice(selectedOrder.subtotal_cents)}</span></div>
              <div className="cart-total-row">
                <span>Shipping</span>
                <span>{selectedOrder.shipping_cents === 0 ? 'FREE' : formatPrice(selectedOrder.shipping_cents)}</span>
              </div>
              <div className="cart-total-row grand"><span>Total</span><span>{formatPrice(selectedOrder.total_cents)}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* ── Addresses Tab ── */}
      {tab === 'addresses' && (
        <div style={{ maxWidth: '420px' }}>
          <h3 style={{ marginBottom: '16px' }}>Shipping Address</h3>
          {addressMsg && <p style={{ color: 'var(--success)', marginBottom: '16px' }}>{addressMsg}</p>}
          <form onSubmit={handleAddressSave}>
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input className="form-input" value={addressForm.name} onChange={e => setAddressForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Address</label>
              <input className="form-input" value={addressForm.line1} onChange={e => setAddressForm(f => ({ ...f, line1: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Apt / Suite</label>
              <input className="form-input" value={addressForm.line2} onChange={e => setAddressForm(f => ({ ...f, line2: e.target.value }))} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">City</label>
                <input className="form-input" value={addressForm.city} onChange={e => setAddressForm(f => ({ ...f, city: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">State</label>
                <input className="form-input" value={addressForm.state} onChange={e => setAddressForm(f => ({ ...f, state: e.target.value }))} maxLength={2} />
              </div>
              <div className="form-group">
                <label className="form-label">ZIP</label>
                <input className="form-input" value={addressForm.zip} onChange={e => setAddressForm(f => ({ ...f, zip: e.target.value }))} />
              </div>
            </div>
            <button className="btn btn-primary" type="submit">Save Address</button>
          </form>
        </div>
      )}

      {/* ── Profile Tab ── */}
      {tab === 'profile' && (
        <div style={{ maxWidth: '420px' }}>
          {profileMsg && <p style={{ color: 'var(--success)', marginBottom: '16px' }}>{profileMsg}</p>}
          <form onSubmit={handleProfileSave}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" value={user.email} disabled style={{ background: 'var(--light-gray)' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="form-input" value={profileForm.name} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">New Password (leave blank to keep)</label>
              <input className="form-input" type="password" value={profileForm.password} onChange={e => setProfileForm(f => ({ ...f, password: e.target.value }))} minLength={6} />
            </div>
            <button className="btn btn-primary" type="submit">Save Changes</button>
          </form>
        </div>
      )}
    </div>
  );
}
